import BankTransferSvgUrl from "@icons/bank-transfer.svg?url";
import SubscriptionSvgUrl from "@icons/subscription.svg?url";
import { Trans } from "@lingui/react/macro";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import React from "react";
import { ErrorBoundary } from "react-error-boundary";

import { useCompany } from "@/common/providers/company-provider";
import { useSidebar } from "@/common/providers/sidebar-provider/sidebar-provider";
import { currencyFormatter, getPrice } from "@/common/services/formatter";
import { invariant } from "@/common/utils/invariant";
import { getLocalizedPaymentStatus } from "@/common/utils/payment-status";
import { getLocalizedPeriods } from "@/common/utils/period";
import {
  getCompanyPayments,
  PaymentToStatus,
  PaymentToType,
  PlanPeriodToType,
} from "@/generated/api/users";

import { Failed } from "../info/info";
import { Pagination } from "../pagination/pagination";
import { Spinner } from "../spinner/spinner";

const Payments = () => {
  const [page, setPage] = React.useState(1);
  const { company } = useCompany();
  invariant(company);
  const payments = useSuspenseQuery(paymentsQuery(company.id, { page }));

  return (
    <>
      <ul className="flex flex-col gap-y-4">
        {payments.data.data.map((payment) => (
          <Payment
            amount={payment.amount}
            date={DateTime.fromISO(payment.createdAt)}
            id={payment.id}
            key={payment.id}
            period={payment.subscription.planPeriod}
            planType={payment.subscription.planType}
            status={payment.status}
            type={payment.type}
          />
        ))}
      </ul>
      <Pagination
        getter={() => page}
        pageSize={5}
        setter={setPage}
        total={payments.data.totalSize}
      />
    </>
  );
};

const Payment = ({
  amount,
  date,
  id,
  period,
  planType,
  type,
  status,
}: {
  amount: string;
  date: DateTime;
  id: number;
  period?: PlanPeriodToType;
  planType: string;
  status: PaymentToStatus;
  type: PaymentToType;
}) => {
  const { actions } = useSidebar();

  return (
    <li>
      <button
        className="text-can-midnight-steel shadow-can-light-box text-left focus:shadow-can-light-box flex w-full flex-col gap-y-2 rounded-2xl p-4"
        onClick={() => actions.openPaymentDetail({ paymentId: id })}
      >
        <span className="flex w-full items-center gap-x-2">
          <img
            src={
              type === "BANK_TRANSFER" ? BankTransferSvgUrl : SubscriptionSvgUrl
            }
          />
          <strong>{amount}</strong>
          <Status status={status} />
        </span>
        <span className="text-xs font-semibold">
          {planType} {period ? `- ${getLocalizedPeriods()[period]}` : null}
        </span>
        <span className="text-can-slate-blue-gray text-xs">
          {date.toLocaleString(DateTime.DATETIME_SHORT)}
        </span>
      </button>
    </li>
  );
};

const Status = ({ status }: { status: PaymentToStatus }) => {
  let item: { gradient: string; text: React.ReactNode } = {
    text: getLocalizedPaymentStatus()[status],
  } as typeof item;

  switch (status) {
    case "ERROR": {
      item = {
        ...item,
        // eslint-disable-next-line lingui/no-unlocalized-strings
        gradient: "from-[#F99B87] to-[#E55743]",
      };
      break;
    }
    case "WAITING": {
      item = {
        ...item,
        // eslint-disable-next-line lingui/no-unlocalized-strings
        gradient: "from-can-tranquil-azure to-can-mystic-aqua",
      };
      break;
    }
    case "REFUNDED":
    case "COMPLETED": {
      item = {
        ...item,
        // eslint-disable-next-line lingui/no-unlocalized-strings
        gradient: "from-[#84D4A3] to-[#51B678]",
      };
      break;
    }
    case "CANCELLED":
    case "INITIALIZED": {
      item = {
        ...item,
        // eslint-disable-next-line lingui/no-unlocalized-strings
        gradient: "from-[#FFC684] to-[#FF914D]",
      };
      break;
    }
    default: {
      const exhaustiveCheck: never = status;
      throw new Error(`Unhandled status case: ${exhaustiveCheck}`);
    }
  }

  return (
    <small
      className={`ml-auto rounded-sm bg-linear-to-r ${item.gradient} p-1.5 text-right text-xs font-normal text-white`}
    >
      {item.text}
    </small>
  );
};

const select = (data: Awaited<ReturnType<typeof getCompanyPayments>>) => {
  return {
    ...data,
    data: data.data.map(({ currency, ...payment }) => ({
      ...payment,
      amount: currencyFormatter(currency.type, {
        maximumFractionDigits: 0,
      }).format(getPrice(payment.amount, currency.precision)),
    })),
  };
};
const paymentsQuery = (
  companyId: number,
  {
    page,
  }: Pick<Exclude<Parameters<typeof getCompanyPayments>[1], undefined>, "page">,
) =>
  queryOptions({
    queryFn: ({ signal }) =>
      getCompanyPayments(
        companyId,
        { page, size: 5, sortDir: "DESC" },
        { signal },
      ),
    queryKey: ["payments", companyId, page],
    select,
  });

const PaymentsTree = () => (
  <ErrorBoundary fallbackRender={({ error }) => <Failed error={error} />}>
    <React.Suspense fallback={<Spinner />}>
      <h2 className="text-can-forest-teal font-bold">
        <Trans>Platby a faktury</Trans>
      </h2>
      <Payments />
    </React.Suspense>
  </ErrorBoundary>
);

export { PaymentsTree as Payments };
